import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import { Container, Card, CardContent, Box, Alert, Divider } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// components
import BreadCrumb from '../components/breadcrumb/BreadCrumbNavigation';

// sections
import ProfileForm from '../sections/profile/ProfileForm';
import ProfileHeader from '../sections/profile/ProfileHeader';

// services
import { createUserProfile, getUserProfile, upsertUserProfile } from '../services/profileService';
import { fileUpload } from '../services/fileUploadService';
import { checkComponentPermission } from '../utils/share';
import { PHONE_REGEX } from '../Constants/RegexConstants';
import Loading from '../layouts/loading';
import { fDate } from '../utils/formatDate';
// ----------------------------------------------------------------------

const ProfileSchema = Yup.object().shape({
  fullName: Yup.string().min(2, 'Too Short!').required('Full name is required'),
  gender: Yup.string().required('Gender is required'),
  phoneNo: Yup.string()
    .matches(PHONE_REGEX, 'Invalid phone number format')
    .min(10, 'Phone number must be at least 10 digits')
    .max(10, 'Phone number must not exceed 10 characters')
    .required('Phone number is required'),
  dateOfBirth: Yup.date().nullable().typeError('Date of Birth is required').required('Date of Birth is required'),
  jobTitle: Yup.string().min(2, 'Too Short!').required('Job title is required'),
  employmentTitle: Yup.string().min(2, 'Too Short!').required('Employment title is required'),
  joiningDate: Yup.date().typeError('Joining date is required').required('Joining date is required'),
  emergencyContactPersonName: Yup.string().min(2, 'Too Short!').required('Contact person name is required'),
  emergencyContact: Yup.string()
    .matches(PHONE_REGEX, 'Invalid phone number format')
    .min(10, 'Emergency contact must be at least 10 digits')
    .max(10, 'Emergency contact must not exceed 10 characters')
    .required('Emergency contact is required'),
});

export default function ProfilePage() {
  const { userId } = useParams();
  const currentUser = useSelector((state) => state?.login?.user || null);
  const currentUserId = currentUser?.id;

  const [profileData, setProfileData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  // Check if user can edit this profile
  const canEdit = !userId || userId === currentUserId?.toString() || checkComponentPermission('EDIT_USER');

  const formik = useFormik({
    initialValues: {
      fullName: '',
      gender: '',
      phoneNo: '',
      dateOfBirth: null,
      jobTitle: '',
      employmentTitle: '',
      joiningDate: null,
      emergencyContact: '',
      emergencyContactPersonName: '',
      profileImage: '',
    },
    validationSchema: ProfileSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const profileDataToSubmit = {
          ...values,
          dateOfBirth: values.dateOfBirth ? fDate(values.dateOfBirth, 'yyyy-MM-dd') : null,
          joiningDate: values.joiningDate ? fDate(values.joiningDate, 'yyyy-MM-dd') : null,
        };

        profileDataToSubmit.profileImage = profileImageFile;

        await submitProfileData(profileDataToSubmit);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to update profile');
        console.error('Profile update error:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchProfileData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      if (userId) {
        response = await getUserProfile(userId);
      } else {
        response = await getUserProfile(currentUserId);
      }
      if (response?.data?.success) {
        setUserData(response?.data?.user);
        if (response?.data?.profile) {
          setProfileData(response.data.profile);

          // Update form values with dayjs objects for DatePicker
          const formValues = response.data.profile;
          formik.resetForm({ values: formValues });
        } else {
          const formValues = { ...formik.initialValues };
          formValues.fullName = response.data.user.userName;
          formik.resetForm({ values: formValues });
        }
      } else {
        setError(response?.message || 'Failed to fetch profile');
      }
    } catch (error) {
      setError(error?.response?.data?.message || 'Failed to fetch profile');
      console.error('Profile fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [userId]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (originalImage) {
      profileData.profileImage = originalImage;
      setOriginalImage(null);
    }
    setIsEditing(false);
    setProfileImageFile(null);
    setProfileImagePreview(null);

    if (profileData) {
      const formValues = profileData;
      formik.resetForm({ values: formValues });
    } else {
      formik.resetForm({ values: formik.initialValues });
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      formik.setSubmitting(true);
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, etc.)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      if (file.name.length > 100) {
        toast.error('File name is too long. Please rename the file.');
        return;
      }

      const response = await fileUpload('profile_image', file.name, file);

      if (response && response.key && response.getUrl) {
        setProfileImageFile(response.key);
        setProfileImagePreview(response.getUrl);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error?.response?.data?.message || 'Failed to upload image. Please try again.');
      event.target.value = '';
    } finally {
      formik.setSubmitting(false);
    }
  };

  const removeImage = () => {
    try {
      setProfileImageFile(null);
      setOriginalImage(profileData?.profileImage || null);
      if(profileData){
        profileData.profileImage = '';
      }
      setProfileImagePreview(null);
      formik.setFieldValue('profileImage', '');
      toast.success('Profile image removed successfully');
    } catch (error) {
      console.log(error.message)
      console.error('Error removing image:', error);
      toast.error('Failed to remove image. Please try again.');
    }
  };

  const submitProfileData = async (profileDataToSubmit) => {
    try {
      if (profileDataToSubmit.profileImage === '' || profileDataToSubmit.profileImage === null) {
        if (profileData?.profileImage) {
          delete profileDataToSubmit.profileImage;
        }
      }

      let response;
      if (userId) {
        if (profileData) {
          response = await upsertUserProfile(userId, profileDataToSubmit);
        } else {
          response = await createUserProfile(userId, profileDataToSubmit);
        }
      } else if (profileData) {
        response = await upsertUserProfile(currentUserId, profileDataToSubmit);
      } else {
        response = await createUserProfile(currentUserId, profileDataToSubmit);
      }

      if (response) {
        toast.success('Profile updated successfully');
        setIsEditing(false);
        setProfileImageFile(null);
        setProfileImagePreview(null);
        setOriginalImage(null);
        fetchProfileData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update profile');
      console.error('Profile update error:', error);
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: userId ? 'Users' : 'Profile', path: userId ? '/users' : '/profile' },
    { label: userId ? `${userData?.userName || 'User'} Profile` : 'My Profile', path: '' },
  ];

  if (isLoading) {
    return <Loading open={isLoading} message="Loading profile..." variant="default" />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <BreadCrumb breadcrumbs={breadcrumbs} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Helmet>
        <title>{userId ? `${userData?.userName || 'User'} Profile` : 'My Profile'}</title>
      </Helmet>

      <Container maxWidth="lg">
        <BreadCrumb breadcrumbs={breadcrumbs} />

        <Box sx={{ mt: 1 }}>
          <Card elevation={0} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
            <CardContent sx={{ padding: 0 }}>
              <ProfileHeader
                userId={userId}
                userData={userData}
                isEditing={isEditing}
                canEdit={canEdit}
                isSubmitting={formik.isSubmitting}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSubmit={formik.handleSubmit}
              />

              <Divider sx={{ mb: 3 }} />

              <ProfileForm
                formik={formik}
                userId={userId}
                userData={userData}
                profileImagePreview={profileImagePreview}
                profileImage={formik.values.profileImage}
                isEditing={isEditing}
                canEdit={canEdit}
                isSubmitting={formik.isSubmitting}
                onImageUpload={handleImageUpload}
                onRemoveImage={removeImage}
              />
            </CardContent>
          </Card>
        </Box>
      </Container>
    </LocalizationProvider>
  );
}
